import GAS from "./GAS";
import React, { useState, useEffect, createContext, useCallback } from "react";

/**
 * Context for the application state
 */
export const Context = createContext();

/**
 * Component for storing state in the app context.
 * Declare {AppProvider} as a parent component in the main application component {App}
 * 
 * @example Access can be done by declaring the following in the rendering of any other component within {App}:
 * <Context.Consumer>
 *   {(context) => ( context.state... )}
 * </Context.Consumer>
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
const AppProvider = ({ children }) => {
    const [usuario, setUsuario] = useState(undefined);
    const [metamodeloAvaliativo, setMetamodeloAvaliativo] = useState(undefined);
    const [questionariosModelos, setQuestionariosModelos] = useState(undefined);
    const [questionarios, setQuestionarios] = useState(undefined);
    const [escalasAvaliativa, setEscalasAvaliativa] = useState(undefined);
    const [membroCPA, setMembroCPA] = useState(undefined);
    const [paineis, setPaineis] = useState(undefined);

    const gasObj = GAS.getInstance();

    /**
     * Loads the logged-in user by requesting from back-end via {GAS.request()}
     * @param {string} email - User email
     * @param {boolean} isMembro - Flag indicating if user is a CPA member
     */
    const loadParticipante = useCallback(async (email, isMembro) => {
        if (!email) return;

        if (isMembro) {
            setUsuario({ email, autorizado: true });
        } else {
            try {
                await gasObj.request('MCPAParticipante.instance.SERVICE.GETParticipante', email);
                setUsuario({ email, autorizado: true });
            } catch (e) {
                setUsuario({ email, autorizado: false });
            }
        }
    },[gasObj]);

    /**
     * Loads available and answered questionnaires by requesting from back-end
     * @param {string} email - User email
     */
    const loadQuestionarios = useCallback( async (email) => {
        if (!email) return;

        try {
            const response = await gasObj.request('MCPAQuestionario.instance.SERVICE.GETListarQuestionariosParticipante', email);
            setQuestionarios(response.response);
        } catch (e) {
            console.error("Error loading questionnaires:", e);
        }
    },[gasObj]);

    /**
     * Loads appropriate panels according to the logged-in user
     * @param {string} email - User email
     */
    const loadPaineisApropriadores = useCallback(async (email) => {
        if (!email) return;

        try {
            const response = await gasObj.request('MCPAPainelApropriador.instance.SERVICE.GETListarPaineisApropriadores', email);
            console.log("Paineis carregados:", response);
            setPaineis(response.response);
        } catch (e) {
            console.error("Error loading panels:", e);
        }
    },[gasObj]);

    /**
     * Loads evaluation metamodel
     */
    const loadMetamodeloAvaliativo = useCallback(async () => {
        try {
            const response = await gasObj.request('MCPAMetamodelo.instance.SERVICE.GETMetamodelo');
            setMetamodeloAvaliativo(response.response);
        } catch (e) {
            console.error("Error loading metamodel:", e);
        }
    },[gasObj]);

    /**
     * Loads questionnaire models
     */
    const loadQuestionariosModelos = useCallback(async () => {
        try {
            const response = await gasObj.request('MCPAQuestionario.instance.SERVICE.GETListarModelosQuestionarios');
            setQuestionariosModelos(response.response);
        } catch (e) {
            console.error("Error loading questionnaire models:", e);
        }
    },[gasObj]);

    /**
     * Loads evaluation scales
     */
    const loadEscalasAvaliativas = useCallback(async () => {
        try {
            const response = await gasObj.request('MCPAEscalaAvaliativa.instance.SERVICE.GETescalaAvaliativa');
            setEscalasAvaliativa(response.response);
        } catch (e) {
            console.error("Error loading evaluation scales:", e);
        }
    },[gasObj]);

    /**
     * Checks if the logged-in user is a CPA member
     * @param {string} email - User email
     * @returns {Promise<boolean>}
     */
    const loadMembroCPA =useCallback(async (email) => {
        if (!email) return false;

        try {
            const response = await gasObj.request('MCPAMembroCPA.instance.SERVICE.GETVerificarMembroCPA', email);
            const isMembro = response.response;
            setMembroCPA(isMembro);

            if (isMembro) {
                setUsuario(currentUser => {
                    return { ...currentUser, autorizado: true };
                });
            }

            return isMembro;
        } catch (e) {
            console.error("Error checking CPA membership:", e);
            return false;
        }
    },[gasObj]);

    /**
     * Loads all application data
     * @param {string} email - User email
     */
    const loadAllData = useCallback(async (email) => {
        if (!email) return;

        try {
            // Load member status and pass it to loadParticipante
            const isMembro = await loadMembroCPA(email);
            await loadParticipante(email, isMembro);

            // Parallel loading for independent operations
            await Promise.all([
                loadQuestionarios(email),
                loadPaineisApropriadores(email),
                loadMetamodeloAvaliativo(),
                loadQuestionariosModelos(),
                loadEscalasAvaliativas()
            ]);
        } catch (error) {
            console.error("Error loading application data:", error);
        }
    },[loadMembroCPA, loadParticipante, loadQuestionarios, loadPaineisApropriadores, loadMetamodeloAvaliativo, loadQuestionariosModelos, loadEscalasAvaliativas]);

    // Called after the component finishes rendering
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await gasObj.request('MCPAParticipante.instance.SERVICE.GETUsuario');
                const usuarioLogadoViaGoogleEmail = response.response;

                if (usuarioLogadoViaGoogleEmail) {
                    await loadAllData(usuarioLogadoViaGoogleEmail);
                } else {
                    setUsuario({ email: undefined, autorizado: false });
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
                setUsuario({ email: undefined, autorizado: false });
            }
        };

        fetchData();
    }, [gasObj, loadAllData]);

    // Render Context.Provider with states and setters available
    return (
        <Context.Provider
            value={{
                usuario,
                setUsuario,
                questionarios,
                setQuestionarios,
                paineis,
                setPaineis,
                metamodeloAvaliativo,
                setMetamodeloAvaliativo,
                questionariosModelos,
                setQuestionariosModelos,
                escalasAvaliativa,
                setEscalasAvaliativa,
                membroCPA,
                setMembroCPA,
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default AppProvider;
